import React, { createContext, useState, useMemo, useEffect } from 'react';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

export const ThemeContext = createContext();
const softColor = main => ({ main, soft: alpha(main, 0.08), softHover: alpha(main, 0.15) });

export const ThemeContextProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    return localStorage.getItem('themeMode') || 'light';
  });

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
    document.body.setAttribute('data-theme', mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: softColor(mode === 'dark' ? '#e79b9f' : '#7a2029'),
          secondary: { main: mode === 'dark' ? '#a7b8d0' : '#52657e' },
          success: softColor(mode === 'dark' ? '#68d3b5' : '#138568'),
          warning: softColor(mode === 'dark' ? '#f6c768' : '#a5680b'),
          error: softColor(mode === 'dark' ? '#ffaaa3' : '#c43d43'),
          background: { default: mode === 'dark' ? '#0d1829' : '#f0f3f8', paper: mode === 'dark' ? '#142238' : '#fff' },
          divider: mode === 'dark' ? '#2b3c54' : '#dce3ed',
          text: { primary: mode === 'dark' ? '#e7edf5' : '#182638', secondary: mode === 'dark' ? '#aebed2' : '#52647a' },
        },
        shape: { borderRadius: 10 },
        zIndex: { modal: 13000, snackbar: 14000, tooltip: 15000 },
        typography: {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
          h6: { fontSize: '1.2rem', fontWeight: 700, letterSpacing: '-0.02em' },
        },
        components: {
          MuiButton: { defaultProps: { disableElevation: true }, styleOverrides: {
            root: { borderRadius: 999, minHeight: 44, whiteSpace: 'nowrap', paddingInline: 20, fontSize: '0.875rem', '&.Mui-focusVisible': { outline: '3px solid #b96c7480', outlineOffset: 3 } },
            sizeSmall: { minHeight: 40, paddingInline: 16 },
          } },
          MuiOutlinedInput: { styleOverrides: {
            root: {
              borderRadius: 12, fontSize: '0.875rem', backgroundColor: mode === 'dark' ? '#1b2c44' : '#f3f6fa',
              '& .MuiOutlinedInput-notchedOutline': { borderWidth: 1, borderColor: mode === 'dark' ? '#637895' : '#aab7c8', borderRadius: 12 },
              '&:hover:not(.Mui-disabled):not(.Mui-error):not(.Mui-focused) .MuiOutlinedInput-notchedOutline': { borderColor: mode === 'dark' ? '#aebed2' : '#52647a' },
              '&.Mui-focused:not(.Mui-error)': { backgroundColor: mode === 'dark' ? '#223752' : '#ffffff', boxShadow: '0 0 0 3px ' + alpha(mode === 'dark' ? '#e79b9f' : '#7a2029', 0.12) },
              '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': { borderColor: mode === 'dark' ? '#e79b9f' : '#7a2029', borderWidth: 2 },
              '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: mode === 'dark' ? '#ffaaa3' : '#c43d43', borderWidth: 2 },
              '&.Mui-disabled': { backgroundColor: mode === 'dark' ? '#16263c' : '#e8edf4' },
              '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: mode === 'dark' ? '#a8b6c9' : '#52647a' },
              '& input::placeholder, & textarea::placeholder': { color: mode === 'dark' ? '#aebed2' : '#52647a', opacity: 1 },
            },
          } },
          MuiDialog: { styleOverrides: { paper: { borderRadius: 24, backgroundImage: 'none', boxShadow: '0 24px 80px rgba(15,23,42,.24)' } } },
          MuiMenu: { styleOverrides: { paper: { borderRadius: 16, marginTop: 6, minWidth: 180, boxShadow: '0 12px 40px rgba(15,23,42,.16)' } } },
          MuiMenuItem: { styleOverrides: { root: { minHeight: 44, fontSize: '0.9375rem' } } },
          MuiTooltip: { defaultProps: { arrow: true } },
          MuiTableCell: { styleOverrides: { root: { fontSize: '0.875rem' }, head: { fontWeight: 600 } } },
        },
      }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
